import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => RichTextEditorComponent),
    multi: true,
  }],
})
export class RichTextEditorComponent implements ControlValueAccessor, AfterViewInit {

  @Input() label = 'Descrição';
  @Input() placeholder = 'Descreva a demanda com detalhes...';
  @Input() minHeight = '220px';

  @ViewChild('editor') editorRef!: ElementRef<HTMLDivElement>;

  disabled = false;
  private pendingValue: string | null = null;
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngAfterViewInit(): void {
    if (this.pendingValue !== null) {
      this.setEditorContent(this.pendingValue);
    }
  }

  writeValue(value: string | null): void {
    this.pendingValue = value;
    if (this.editorRef) {
      this.setEditorContent(value);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  exec(command: string, value?: string): void {
    if (this.disabled) {
      return;
    }
    this.focusEditor();
    document.execCommand(command, false, value);
    this.emitChange();
  }

  destacarTexto(): void {
    this.exec('backColor', '#fff59d');
  }

  onInput(): void {
    this.emitChange();
  }

  onBlur(): void {
    this.onTouched();
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboard = event.clipboardData;
    if (!clipboard) {
      return;
    }
    const html = clipboard.getData('text/html');
    const plain = clipboard.getData('text/plain');
    if (html) {
      document.execCommand('insertHTML', false, this.sanitizeHtml(html));
    } else {
      document.execCommand('insertText', false, plain);
    }
    this.emitChange();
  }

  private focusEditor(): void {
    this.editorRef?.nativeElement?.focus();
  }

  private emitChange(): void {
    if (!this.editorRef) {
      return;
    }
    const html = this.editorRef.nativeElement.innerHTML.trim();
    this.onChange(this.isEmpty(html) ? '' : html);
  }

  private setEditorContent(value: string | null): void {
    if (!this.editorRef) {
      return;
    }
    this.editorRef.nativeElement.innerHTML = this.normalizeContent(value);
  }

  private normalizeContent(value: string | null): string {
    if (!value) {
      return '';
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    if (/<[a-z][\s\S]*>/i.test(trimmed)) {
      return this.sanitizeHtml(trimmed);
    }
    return trimmed
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
  }

  private isEmpty(html: string): boolean {
    const texto = html
      .replace(/<br\s*\/?>/gi, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/<[^>]+>/g, '')
      .trim();
    return !texto;
  }

  private sanitizeHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('script, style, iframe, object, embed').forEach(el => el.remove());
    doc.querySelectorAll('*').forEach(el => {
      Array.from(el.attributes).forEach(attr => {
        if (attr.name.toLowerCase().startsWith('on')) {
          el.removeAttribute(attr.name);
        }
      });
    });
    return doc.body.innerHTML;
  }
}
